import React, {useEffect, useState} from 'react';
import Header from '../../components/Header';
import {Footer} from '../../components/Footer';
import {
    BetInSchema,
    BetSchema,
    BetsService,
    BetWithUserSchema,
    MatchesService,
    MatchWithBetsSchema,
    UserScoreSchema
} from "../../api";
import {useParams} from "react-router-dom";
import {useAlert} from "../../contexts/AlertContext";
import {BetResultTooltip} from "../../components/BetResultTooltipProps/BetResultTooltipProps";
import {Skeleton} from "../../components/Skeleton";
import {Rating} from "../../components/Rating";
import {MatchCard} from "../../components/MatchCard";

function Match() {
    const [match, setMatch] = useState<MatchWithBetsSchema | undefined>(undefined);
    const [place, setPlace] = useState<UserScoreSchema>();
    const [editingBet, setEditingBet] = useState<BetInSchema>();
    const [betLoading, setBetLoading] = useState<boolean>(false);
    const { matchId } = useParams();
    const { infoTip, errorTip } = useAlert();
    const [loading, setLoading] = useState<boolean>(true);

    useEffect(() => {
        Promise.all([
            BetsService.getUserScore(),
            MatchesService.getMatch(parseInt(matchId!)),
        ]).then(([userScoreData, matchData]) => {
            setPlace(userScoreData);
            setMatch({...matchData});
        }).finally(() => setLoading(false));
    }, []);

    const allBets: BetWithUserSchema[] = React.useMemo(() => {
        if (!match) return [];
        const list: BetWithUserSchema[] = [];
        if (match.bet) {
            list.push({
                ...match.bet,
                owner: {
                    id: place?.user?.id ?? 0,
                    username: place?.user?.username ?? 'Вы',
                    about: place?.user?.about,
                    avatar: place?.user?.avatar,
                },
            } as BetWithUserSchema);
        }
        if (match.bets) {
            for (const b of match.bets) {
                if (b.owner.id !== place?.user?.id) {
                    list.push(b);
                }
            }
        }
        return list;
    }, [match, place]);

    const showAllBets = match && (
        match.status !== 'NOT_STARTED' ||
        new Date(match.scheduled_at).getTime() <= Date.now()
    );

    const handleSubmitBet = () => {
        if (!editingBet || !match) return;
        setBetLoading(true);

        const response = match.bet
            ? BetsService.updateBet(match.bet.id!, editingBet)
            : BetsService.createBet(editingBet);

        response
            .then((data: BetSchema) => {
                setMatch({
                    ...match,
                    bet: {
                        id: data.id,
                        home: data.home,
                        away: data.away,
                        overtime_home: data.overtime_home,
                        overtime_away: data.overtime_away,
                        penalty_home: data.penalty_home,
                        penalty_away: data.penalty_away,
                        stake: data.stake,
                        is_all_in: data.is_all_in,
                        is_golden: data.is_golden,
                        classic_points: data.classic_points,
                        stake_profit: data.stake_profit,
                    },
                });
                BetsService.getUserScore().then((data) => setPlace(data));
            })
            .then(() => infoTip('Ставка сделана!'))
            .then(() => setEditingBet(undefined))
            .catch((err) => {
                errorTip(err.body?.detail ?? err.toString());
                return Promise.reject(err);
            })
            .finally(() => setBetLoading(false));
    };

    const formatScore = (bet: BetSchema | BetWithUserSchema) => {
        if (bet.home === null || bet.away === null) {
            return <>&mdash;</>
        }
        const parts: string[] = [`${bet.home}:${bet.away}`];
        if (bet.overtime_home !== null && bet.overtime_away !== null) {
            parts.push(`(${bet.overtime_home}:${bet.overtime_away})`);
        }
        if (bet.penalty_home !== null && bet.penalty_away !== null) {
            parts.push(`пен. ${bet.penalty_home}:${bet.penalty_away}`);
        }
        return parts.join(' ');
    };
    if (loading) return <Skeleton />;
    return (
        <>
            <Header />
            {
                match &&
                <div className='tournament'>
                    {place && <Rating place={place} />}
                    <h2 className='tournament__title'>Прогноз на матч</h2>
                    <p className='tournament__subtitle'>Прогнозы и ставки игроков на этот матч.</p>
                </div>
            }
            {
                match &&
                <div className='matches'>
                    <MatchCard
                        match={match}
                        place={place}
                        editingBet={editingBet}
                        betLoading={betLoading}
                        onEdit={setEditingBet}
                        onCancel={() => setEditingBet(undefined)}
                        onSave={handleSubmitBet}
                        showDetails={false}
                    />
                </div>
            }

            {/* Таблица ставок всех игроков */}
            {allBets.length > 0 && (
                <section className='rates'>
                    <h3 className='rates__title'>
                        {showAllBets ? 'Ставки игроков' : 'Кто поставил'}
                    </h3>
                    <div className='rates__scroll'>
                        <table className='rates__table rates__table--match'>
                            <thead>
                                <tr>
                                    <th>Игрок</th>
                                    <th className='rates__cell--center'>Прогноз</th>
                                    <th className='rates__cell--center'>Ставка</th>
                                    {showAllBets && (
                                        <th className='rates__cell--right'>Итог</th>
                                    )}
                                </tr>
                            </thead>
                            <tbody>
                                {allBets.map((bet) => {
                                    const isMe = bet.owner.id === place?.user?.id;
                                    const totalResult = (bet.classic_points ?? 0) + (bet.stake_profit ?? 0);

                                    return (
                                        <tr key={bet.id} className={isMe ? 'rates__row--me' : ''}>
                                            <td>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                    {bet.owner.avatar && (
                                                        <img
                                                            src={bet.owner.avatar}
                                                            alt={bet.owner.username}
                                                            style={{
                                                                width: 24,
                                                                height: 24,
                                                                borderRadius: '50%',
                                                                objectFit: 'cover',
                                                            }}
                                                        />
                                                    )}
                                                    <a href={`/user/${bet.owner.id}`} className='rates__user-name-link'>
                                                        {isMe ? 'Вы' : bet.owner.username}
                                                    </a>
                                                </div>
                                            </td>
                                            <td className='rates__cell--center rates__cell--score'>
                                                {formatScore(bet)}
                                            </td>
                                            <td className='rates__cell--center'>
                                                {bet.stake && bet.stake > 0 ? (
                                                    <>
                                                        {bet.stake}
                                                        {bet.is_all_in && (
                                                            <span className='rates__cell__badge rates__cell__badge--allin'>ALL IN</span>
                                                        )}
                                                        {bet.is_golden && (
                                                            <span className='rates__cell__badge rates__cell__badge--golden'>×2</span>
                                                        )}
                                                    </>
                                                ) : (
                                                    <span className='rates__cell--empty'>—</span>
                                                )}
                                            </td>
                                            {showAllBets && (
                                                <td className={`rates__cell--right ${
                                                    bet.classic_points !== null
                                                        ? totalResult >= 0
                                                            ? 'rates__cell--positive'
                                                            : 'rates__cell--negative'
                                                        : 'rates__cell--empty'
                                                }`}>
                                                    {bet.classic_points !== null ? (
                                                        <BetResultTooltip bet={bet} totalResult={totalResult}>
                                                            {`${totalResult >= 0 ? '+' : ''}${totalResult}`}
                                                        </BetResultTooltip>
                                                    ) : (
                                                        '—'
                                                    )}
                                                </td>
                                            )}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </section>
            )}
            <Footer />
        </>
    );
}
export { Match };
